import React, { useContext, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { Button } from 'semantic-ui-react';

import classNames from 'classnames';

import '../css/BasicIframeModalLauncherButton.less';

import { SatCoreRegister } from '../SatCoreRegistry';

import { DIALOG_NAMES } from '../managers/DialogManager';

import { useLauncherOrigin } from '../hooks/useLauncherOrigin';

import BasicIframeModalLauncherService from '../services/BasicIframeModalLauncherService';

/**
 * BasicIframeModalLauncherButton - A button trigger for BasicIframeModal
 *
 * @description
 * A button component that opens BasicIframeModal with a specified external URL.
 *
 * @example
 * // Minimal example 1 - basic button launcher with predefined url
 * <BasicIframeModalLauncherButton
 *   url='https://www.example.com'
 *   buttonLabel={t('openExternalWebsite')}
 *   headerTitle={t('externalWebsite')}
 * />
 *
 * @example
 * // Minimal example 2 - basic button launcher via iframeModalLauncherObj
 * <BasicIframeModalLauncherButton
 *   iframeModalLauncherObj={iframeModalLauncherObj)}
 *   buttonLabel={t('gettingStartedModalLauncherButtonLabel')}
 *   headerTitle={t('gettingStartedModalHeaderTitle')}
 * />
 *
 * @example
 * // Maximum options example - fully customized
 * <BasicIframeModalLauncherButton
 *   url='https://codesandbox.io/embed/your-demo'
 *   buttonLabel={t('interactiveDemoButton')}
 *   headerTitle={t('interactiveDemoTitle')}
 *   initWidth={1400}
 *   initHeight={900}
 *   disabled={false}
 *   closeOnDimmerClick={false}
 *   closeOnEscape={false}
 *   iframeProps={{
 *     allow: 'accelerometer; camera; encrypted-media; geolocation; gyroscope; microphone',
 *     sandbox: 'allow-scripts allow-same-origin allow-forms',
 *     loading: 'lazy',
 *     referrerPolicy: 'no-referrer'
 *   }}
 *   onOpen={() => console.info('Modal opened')}
 *   onClose={() => console.info('Modal closed')}
 *   className='custom-launcher-premium'
 *   style={{ margin: '20px', padding: '10px' }}
 * />
 */
const BasicIframeModalLauncherButton = observer((props = {}) => {
  const {
    basic = false,
    buttonLabel,
    className = '',
    closeOnDimmerClick = false,
    closeOnEscape = true,
    disabled = false,
    headerTitle,
    iframeModalLauncherObj = {},
    iframeProps,
    initHeight,
    initWidth,
    onClose = () => {},
    onOpen = () => {},
    primary = true,
    style = {},
    t,
    top
  } = props;

  const {
    dialogManager
  } = useContext(MobXProviderContext);

  const { ref: launcherWrapperRef, getRect: getOriginRect } = useLauncherOrigin();

  const [isLoading, setIsLoading] = useState(false);

  const handleLaunchModal = async () => {
    if (disabled || isLoading) {
      return;
    }

    const openedDialog = dialogManager.openedDialogs?.get(DIALOG_NAMES.BASIC_IFRAME_MODAL);
    const isSameLauncherOpen = openedDialog?.data?.launcherId === iframeModalLauncherObj?._id;

    if (isSameLauncherOpen) {
      // Same launcher clicked while modal is open — trigger the modal's registered
      // animated close (falls back to plain closeDialog if the modal didn't register).
      await dialogManager.closeDialogAnimatedIfPossible(DIALOG_NAMES.BASIC_IFRAME_MODAL);
      return;
    }

    setIsLoading(true);
    try {
      await BasicIframeModalLauncherService.openIframeModalFromLauncher(
        dialogManager,
        iframeModalLauncherObj, {
          closeOnDimmerClick,
          closeOnEscape,
          getOriginRect,
          headerTitle,
          iframeProps,
          initHeight,
          initWidth,
          onClose,
          onOpen,
          top,
          url: props?.url
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const buttonText = buttonLabel || t('openExternalContent');

  return (
    <div
      className={classNames('BasicIframeModalLauncherButton', className)}
      style={style}>
      <div ref={launcherWrapperRef} className='basic-iframe-modal-launcher-button-wrapper'>
        <Button
          aria-label={buttonText}
          basic={basic}
          disabled={disabled}
          loading={isLoading}
          onClick={handleLaunchModal}
          primary={primary}>
          {buttonText}
        </Button>
      </div>
    </div>
  );
});

export default BasicIframeModalLauncherButton;

SatCoreRegister('BasicIframeModalLauncherButton', BasicIframeModalLauncherButton);
